import { formatCurrency, formatDateTime } from "../utils";
import companyLogo from "../assets/images/logo-transparent.png";

export const getCurrentStatementPeriod = () => {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const formatDate = (date) =>
    date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  return `${formatDate(monthStart)} - ${formatDate(monthEnd)}`;
};

const getTransactionsSummary = (transactions) =>
  transactions.reduce(
    (acc, transaction) => {
      const amount = Number(transaction.amount || 0);

      if ((transaction.entry_type || "").toLowerCase() === "credit") {
        acc.totalCredits += amount;
      }

      if ((transaction.entry_type || "").toLowerCase() === "debit") {
        acc.totalDebits += amount;
      }

      acc.netMovement = acc.totalCredits - acc.totalDebits;
      return acc;
    },
    {
      totalCredits: 0,
      totalDebits: 0,
      netMovement: 0,
    },
  );

const formatTransactionType = (value) => {
  if (!value) return "-";
  if (value === "internal_power_purchase") return "Power Purchase";
  if (value === "auto_recovered_debt") return "Recovered Debt";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const normalizePdfText = (value) =>
  String(value ?? "")
    .replace(/₦/g, "NGN ")
    .replace(/â‚¦/g, "NGN ")
    .replace(/[^\x20-\x7E]/g, " ");

const escapePdfText = (value) =>
  normalizePdfText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const splitText = (value, maxLength = 34) => {
  const text = normalizePdfText(value);
  const lines = [];

  for (let index = 0; index < text.length; index += maxLength) {
    lines.push(text.slice(index, index + maxLength));
  }

  return lines.length ? lines : ["--"];
};

const formatPdfDateTime = (value) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")} ${String(
    date.getHours(),
  ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const loadPdfLogoImage = () =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const targetWidth = 180;
      const targetHeight = Math.max(
        1,
        Math.round((image.naturalHeight / image.naturalWidth) * targetWidth),
      );
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, targetWidth, targetHeight);
      context.drawImage(image, 0, 0, targetWidth, targetHeight);

      const { data } = context.getImageData(0, 0, targetWidth, targetHeight);
      let hex = "";

      for (let index = 0; index < data.length; index += 4) {
        hex += data[index].toString(16).padStart(2, "0");
        hex += data[index + 1].toString(16).padStart(2, "0");
        hex += data[index + 2].toString(16).padStart(2, "0");
      }

      resolve({
        width: targetWidth,
        height: targetHeight,
        data: `${hex}>`,
      });
    };

    image.onerror = reject;
    image.src = companyLogo;
  });

const buildPdfDocument = ({
  accountId,
  statementPeriodLabel,
  summary,
  rows,
  logoImage,
}) => {
  const pageWidth = 595;
  const pageHeight = 842;
  const margin = 36;
  const bottomMargin = 42;
  const rowHeight = 18;
  const tableColumns = [
    { label: "S/N", x: 36 },
    { label: "Reference", x: 58 },
    { label: "Entry Type", x: 160 },
    { label: "Transaction Type", x: 220 },
    { label: "Amount", x: 390, align: "right" },
    { label: "Closing Bal.", x: 470, align: "right" },
    { label: "Created At", x: 492 },
  ];
  const pages = [];
  let commands = [];
  let cursorY = pageHeight - margin;
  let pageNumber = 1;

  const addText = (text, x, y, size = 10, font = "F1", color = "0 0 0") => {
    commands.push(
      `BT ${color} rg /${font} ${size} Tf 1 0 0 1 ${x} ${y} Tm (${escapePdfText(text)}) Tj ET`,
    );
  };

  const addRightAlignedText = (
    text,
    rightX,
    y,
    size = 10,
    font = "F1",
    color = "0 0 0",
  ) => {
    const normalizedText = normalizePdfText(text);
    const approximateWidth = normalizedText.length * size * 0.52;
    addText(normalizedText, rightX - approximateWidth, y, size, font, color);
  };

  const addLine = (x1, y1, x2, y2) => {
    commands.push(`0.5 w ${x1} ${y1} m ${x2} ${y2} l S`);
  };

  const addImage = (name, x, y, width, height) => {
    commands.push(`q ${width} 0 0 ${height} ${x} ${y} cm /${name} Do Q`);
  };

  const finishPage = () => {
    addText(`Page ${pageNumber}`, pageWidth - 72, 24, 8);
    pages.push(commands.join("\n"));
    commands = [];
    cursorY = pageHeight - margin;
    pageNumber += 1;
  };

  const drawHeader = () => {
    if (logoImage) {
      const logoWidth = 150;
      const logoHeight = Math.round(
        (logoImage.height / logoImage.width) * logoWidth,
      );
      addImage("Logo", margin, cursorY - logoHeight, logoWidth, logoHeight);
      addText("Account Statement", margin, cursorY - logoHeight - 28, 18, "F2");
      cursorY -= logoHeight + 52;
    } else {
      addText("Account Statement", margin, cursorY, 18, "F2");
      cursorY -= 24;
    }

    addText(`Account ID: ${accountId || "--"}`, margin, cursorY, 10);
    cursorY -= 14;
    addText(`Transaction period: ${statementPeriodLabel}`, margin, cursorY, 10);
    cursorY -= 14;
    addText(
      `Generated: ${formatDateTime(new Date().toISOString())}`,
      margin,
      cursorY,
      10,
    );
    cursorY -= 18;
    addLine(margin, cursorY, pageWidth - margin, cursorY);
    cursorY -= 20;
  };

  const drawSummary = () => {
    addText("Summary", margin, cursorY, 12, "F2");
    cursorY -= 16;
    addText(
      `Total Credits: ${formatCurrency(summary.totalCredits)}`,
      margin,
      cursorY,
      10,
    );
    addText(
      `Total Debits: ${formatCurrency(summary.totalDebits)}`,
      220,
      cursorY,
      10,
    );
    cursorY -= 14;
    addText(
      `Net Movement: ${summary.netMovement >= 0 ? "+" : ""}${formatCurrency(summary.netMovement)}`,
      margin,
      cursorY,
      10,
    );
    addText(`Total Transactions: ${rows.length}`, 220, cursorY, 10);
    cursorY -= 22;
  };

  const drawTableHeader = () => {
    addLine(margin, cursorY + 6, pageWidth - margin, cursorY + 6);
    tableColumns.forEach((column) => {
      if (column.align === "right") {
        addRightAlignedText(column.label, column.x, cursorY - 6, 7, "F2");
        return;
      }

      addText(column.label, column.x, cursorY - 6, 7, "F2");
    });
    cursorY -= 18;
    addLine(margin, cursorY + 6, pageWidth - margin, cursorY + 6);
    cursorY -= 10;
  };

  drawHeader();
  drawSummary();
  drawTableHeader();

  rows.forEach((transaction, index) => {
    const referenceLines = splitText(transaction.reference || "--", 16);
    const typeLines = splitText(formatTransactionType(transaction.transaction_type), 14);
    const rowLines = Math.max(referenceLines.length, typeLines.length, 1);
    const neededHeight = Math.max(rowHeight, rowLines * 11 + 8);

    if (cursorY < bottomMargin + neededHeight) {
      finishPage();
      drawTableHeader();
    }

    addText(index + 1, 36, cursorY, 7);
    referenceLines.forEach((line, lineIndex) => {
      addText(line, 58, cursorY - lineIndex * 10, 7);
    });
    addText(
      transaction.entry_type
        ? transaction.entry_type.charAt(0).toUpperCase() + transaction.entry_type.slice(1)
        : "Unknown",
      160,
      cursorY,
      7,
    );
    typeLines.forEach((line, lineIndex) => {
      addText(line, 220, cursorY - lineIndex * 10, 7);
    });
    addRightAlignedText(
      formatCurrency(Number(transaction.amount || 0)),
      390,
      cursorY,
      7,
    );
    addRightAlignedText(
      formatCurrency(Number(transaction.balance_after || 0)),
      470,
      cursorY,
      7,
    );
    addText(formatPdfDateTime(transaction.created_at), 492, cursorY, 7);
    cursorY -= neededHeight;
  });

  finishPage();

  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    `<< /Type /Pages /Kids [ ${pages
      .map((_, index) => `${index + 3} 0 R`)
      .join(" ")} ] /Count ${pages.length} >>`,
  ];
  const pageObjectStart = objects.length + 1;
  const contentObjectStart = pageObjectStart + pages.length;
  const logoObjectNumber = logoImage ? contentObjectStart + pages.length : null;

  pages.forEach((_, index) => {
    const xObjectResources = logoImage
      ? `/XObject << /Logo ${logoObjectNumber} 0 R >>`
      : "";

    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /ProcSet [/PDF /Text /ImageC] ${xObjectResources} /Font << /F1 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> /F2 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >> >> >> /Contents ${
        contentObjectStart + index
      } 0 R >>`,
    );
  });

  pages.forEach((pageCommands) => {
    objects.push(
      `<< /Length ${pageCommands.length} >>\nstream\n${pageCommands}\nendstream`,
    );
  });

  if (logoImage) {
    objects.push(
      `<< /Type /XObject /Subtype /Image /Width ${logoImage.width} /Height ${logoImage.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /ASCIIHexDecode /Length ${logoImage.data.length} >>\nstream\n${logoImage.data}\nendstream`,
    );
  }

  let pdf = "%PDF-1.4\n";
  const offsets = [0];

  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return pdf;
};

export const downloadAccountStatementPdf = async ({
  accountId,
  transactions,
  statementPeriodLabel = getCurrentStatementPeriod(),
}) => {
  const summary = getTransactionsSummary(transactions);
  let logoImage = null;

  try {
    logoImage = await loadPdfLogoImage();
  } catch {
    logoImage = null;
  }

  const pdf = buildPdfDocument({
    accountId,
    statementPeriodLabel,
    summary,
    rows: transactions,
    logoImage,
  });
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `account_statement_${accountId || "account"}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
